import { useState } from "react";
import { publicApi } from "@/lib/api";
import { toast } from "sonner";
import { X, Download, Sparkles, ArrowRight } from "lucide-react";

const LeadModal = ({ open, onClose, title, subtitle, interest = "brochure", ctaLabel = "Get Instant Access" }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await publicApi.lead({ ...form, interest });
      setResult(res);
      setDone(true);
      toast.success("Sent! Check your email for the brochure.");
      if (res.brochure_url) {
        window.open(res.brochure_url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#3B1C1C]/60 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="relative w-full max-w-md bg-[#FDFAF3] rounded-3xl overflow-hidden shadow-[0_40px_100px_rgba(59,28,28,0.35)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          data-cursor="button"
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-[#F5EEDD] hover:bg-[#EBE0C4] flex items-center justify-center text-[#6B1A1A]"
        >
          <X size={16} />
        </button>

        <div className="p-8 md:p-10">
          {!done ? (
            <>
              <div className="flex items-center gap-2 text-[#B58A38] text-[10px] tracking-[0.5em] uppercase">
                <Sparkles size={12} /> Silver Jubilee
              </div>
              <h3 className="font-cinzel text-2xl md:text-3xl text-[#3B1C1C] mt-3 leading-tight">{title}</h3>
              {subtitle && <p className="text-sm text-[#7A6A5C] mt-3 leading-relaxed">{subtitle}</p>}

              <form onSubmit={submit} className="mt-6 space-y-3">
                <input
                  data-testid="lead-name"
                  required
                  type="text"
                  placeholder="Your Name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-full bg-white border border-[#E5D7B8] focus:border-[#B58A38] focus:ring-2 focus:ring-[#B58A38]/15 outline-none text-sm font-poppins"
                />
                <input
                  data-testid="lead-email"
                  required
                  type="email"
                  placeholder="Email Address"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-full bg-white border border-[#E5D7B8] focus:border-[#B58A38] focus:ring-2 focus:ring-[#B58A38]/15 outline-none text-sm font-poppins"
                />
                <input
                  data-testid="lead-phone"
                  type="tel"
                  placeholder="Phone / WhatsApp (Optional)"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-full bg-white border border-[#E5D7B8] focus:border-[#B58A38] focus:ring-2 focus:ring-[#B58A38]/15 outline-none text-sm font-poppins"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="lead-submit"
                  data-cursor="button"
                  className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-gradient-to-r from-[#6B1A1A] via-[#7A2323] to-[#6B1A1A] text-[#F5EEDD] text-[11px] tracking-[0.35em] uppercase hover:shadow-[0_10px_30px_rgba(107,26,26,0.4)] transition-all disabled:opacity-60"
                >
                  <Download size={14} /> {loading ? "Sending…" : ctaLabel}
                </button>
                <p className="text-[10px] text-center text-[#7A6A5C] pt-1">
                  Your details are protected. A Rudralife expert will personally reach out.
                </p>
              </form>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#B58A38]/15 flex items-center justify-center text-[#B58A38]">
                <Sparkles size={26} />
              </div>
              <h3 className="font-cinzel text-2xl text-[#3B1C1C] mt-5">Namaste!</h3>
              <p className="text-sm text-[#7A6A5C] mt-3 leading-relaxed">
                Your exclusive Silver Jubilee brochure is on its way to your inbox. Our panel expert will personally connect with you shortly.
              </p>
              {result?.brochure_url && (
                <a
                  href={result.brochure_url}
                  target="_blank"
                  rel="noreferrer"
                  data-cursor="button"
                  className="mt-5 inline-flex items-center gap-2 py-3 px-6 rounded-full bg-[#3B1C1C] text-[#F5EEDD] text-[11px] tracking-[0.35em] uppercase"
                >
                  <Download size={14} /> Download Now <ArrowRight size={12} />
                </a>
              )}
              <button
                onClick={onClose}
                data-cursor="button"
                className="mt-4 block mx-auto text-xs text-[#B58A38] tracking-widest uppercase hover:text-[#6B1A1A]"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadModal;
